import { useState, useEffect, useCallback, useRef } from 'react';
import type { TFunction } from 'i18next';
import { sanitizeFileName } from '../utils/csvValidation';

interface DownloadModalProps {
  isOpen: boolean;
  defaultFileName: string;
  onConfirm: (customName: string) => void;
  onCancel: () => void;
  t: TFunction;
  isLoading?: boolean;
}

/**
 * Removes .csv extension if present to avoid duplication
 */
function removeCSVExtension(fileName: string): string {
  return fileName.replace(/\.csv$/i, '');
}

export function DownloadModal({
  isOpen,
  defaultFileName,
  onConfirm,
  onCancel,
  t,
  isLoading = false
}: DownloadModalProps) {
  const [customName, setCustomName] = useState<string>('');
  const [hasInvalidChars, setHasInvalidChars] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  // Auto-sanitize and validate input
  const handleInputChange = (value: string) => {
    const containsInvalid = /[/\\:*?"<>|]/.test(value);
    setHasInvalidChars(containsInvalid);

    // Auto-sanitize the input
    const sanitized = sanitizeFileName(value);
    setCustomName(sanitized);
  };

  // Get final filename with .csv extension
  const getFinalFileName = useCallback(() => {
    const name = customName.trim() || defaultFileName;
    const withoutExtension = removeCSVExtension(name);
    return `${withoutExtension}.csv`;
  }, [customName, defaultFileName]);

  const handleConfirm = useCallback(() => {
    if (isLoading) return;

    const finalName = customName.trim() || defaultFileName;
    const withoutExtension = removeCSVExtension(finalName);
    onConfirm(withoutExtension);
    setCustomName('');
    setHasInvalidChars(false);
  }, [customName, defaultFileName, onConfirm, isLoading]);

  const handleCancel = useCallback(() => {
    if (isLoading) return;

    setCustomName('');
    setHasInvalidChars(false);
    onCancel();
  }, [onCancel, isLoading]);

  // Keyboard event handlers
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      } else if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        handleConfirm();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleConfirm, handleCancel]);

  // Focus trap
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Click outside to close
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isLoading) {
      handleCancel();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="download-modal-title"
    >
      <div
        ref={modalRef}
        className="bg-card rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl animate-slideUp"
      >
        {/* Header with icon */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-shrink-0 w-10 h-10 bg-accent rounded-full flex items-center justify-center">
            <svg
              className="w-6 h-6 text-primary"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
              />
            </svg>
          </div>
          <h3
            id="download-modal-title"
            className="text-lg font-semibold text-foreground"
          >
            {t('modal.title')}
          </h3>
        </div>

        {/* Input section */}
        <div className="mb-6">
          <label
            htmlFor="download-filename-input"
            className="block text-sm font-medium text-foreground mb-2"
          >
            {t('modal.fileName')}
          </label>
          <input
            ref={inputRef}
            id="download-filename-input"
            type="text"
            value={customName}
            onChange={(e) => handleInputChange(e.target.value)}
            placeholder={defaultFileName}
            disabled={isLoading}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 transition-colors ${
              hasInvalidChars
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                : 'border-input focus:ring-ring focus:border-primary'
            } ${isLoading ? 'bg-muted cursor-not-allowed' : ''}`}
            aria-describedby="filename-hint filename-preview filename-error"
            aria-invalid={hasInvalidChars}
          />

          {/* Error message for invalid characters */}
          {hasInvalidChars && (
            <p
              id="filename-error"
              className="mt-1 text-xs text-red-600 flex items-center gap-1"
              role="alert"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {t('modal.invalidChars')}
            </p>
          )}

          {/* Hint text */}
          <p
            id="filename-hint"
            className="mt-1 text-xs text-muted-foreground"
          >
            {t('modal.hint')}
          </p>

          {/* Final filename preview */}
          <div
            id="filename-preview"
            className="mt-2 p-2 bg-muted rounded border border-border"
          >
            <p className="text-xs font-medium text-foreground mb-1">
              {t('modal.finalFileName')}:
            </p>
            <p className="text-sm font-mono text-foreground break-all">
              {getFinalFileName()}
            </p>
          </div>
        </div>

        {/* Loading indicator */}
        {isLoading && (
          <div className="mb-4 p-3 bg-accent border border-primary/20 rounded-lg flex items-center gap-2">
            <svg
              className="animate-spin h-5 w-5 text-primary"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span className="text-sm text-accent-foreground">
              {t('modal.downloading')}
            </span>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={handleCancel}
            disabled={isLoading}
            className={`px-4 py-2 text-sm font-medium text-foreground bg-muted rounded-lg hover:bg-muted/80 focus:outline-none focus:ring-2 focus:ring-border focus:ring-offset-2 transition-colors ${
              isLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            aria-label={t('modal.cancel')}
          >
            {t('modal.cancel')}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className={`px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-colors flex items-center gap-2 ${
              isLoading ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            aria-label={t('modal.confirm')}
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {t('modal.downloading')}
              </>
            ) : (
              <>
                <svg
                  className="w-4 h-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                {t('modal.confirm')}
              </>
            )}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }

        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
